import styled from '@emotion/styled';
import { SmallText } from 'ds/components/typography';

// 'ticker' = the v2 MarketTrendTicker dropdown, which on mobile inverts the surface colors
// (grey panel / white cards) and enlarges the small-card figures vs the default (v1 home) look.
// 'default' keeps the legacy v1 appearance so the shared card doesn't regress the live v1 page.
export type CardVariant = 'default' | 'ticker';

export const Wrapper = styled.div<{ isSmall?: boolean; variant?: CardVariant }>(({ theme, isSmall, variant }) => ({
  borderRadius: theme.borderRadius.large,
  background: theme.colors.neutrals.grey10,
  padding: isSmall ? theme.spacing(1.5) : theme.spacing(2),
  display: 'flex',
  flexDirection: 'column',
  ...(variant === 'ticker' ? {
    [theme.media.createObjectMediaQuery({ to: 2 })]: {
      background: theme.colors.neutrals.white,
      padding: theme.spacing(2),
      // Figma small-card group: title and figure are tucked together (2px), the 8px gap to the
      // trend row lives on the Data element below.
      gap: '2px',
    },
  } : {}),
}));

export const Title = styled(SmallText)<{ variant?: CardVariant }>(({ theme, variant }) => ({
  color: theme.colors.neutrals.grey1,
  // v1 (default) keeps its legacy 12px; only the v2 ticker gets the larger 14px/20px figure title.
  fontSize: variant === 'ticker' ? 14 : 12,
  ...(variant === 'ticker' ? { lineHeight: '20px' } : {}),
}));

export const Data = styled.div<{ isSmall?: boolean; variant?: CardVariant }>(({ theme, isSmall, variant }) => ({
  color: theme.colors.neutrals.grey1,
  display: 'flex',
  alignItems: isSmall ? 'start' : 'center',
  justifyContent: 'space-between',
  flexDirection: isSmall ? 'column' : 'row',
  gap: isSmall ? theme.spacing(0.5) : theme.spacing(1),
  ...(variant === 'ticker' && isSmall ? {
    [theme.media.createObjectMediaQuery({ to: 2 })]: {
      gap: theme.spacing(1),
    },
  } : {}),
}));

export const Count = styled.div<{ isSmall?: boolean; variant?: CardVariant }>(({ theme, isSmall, variant }) => ({
  color: theme.colors.neutrals.grey1,
  fontSize: isSmall ? 25 : 40,
  fontWeight: 700,
  // v1 (default) keeps its legacy 'normal' desktop line-height; only the ticker uses the taller 46px.
  lineHeight: isSmall ? '32px' : (variant === 'ticker' ? '46px' : 'normal'),
  ...(variant === 'ticker' && isSmall ? {
    [theme.media.createObjectMediaQuery({ to: 2 })]: {
      fontSize: 32,
      lineHeight: '38px',
    },
  } : {}),
}));

export const Delta = styled.div<{ isSmall?: boolean; variant?: CardVariant }>(({ theme, isSmall, variant }) => ({
  display: 'flex',
  alignItems: 'center',
  // Figma (nodes 11156-53097 / 11164-94828): the trend chip sits to the RIGHT of the
  // "מחודש קודם" label. The page is RTL, so a plain row would place the first child (label)
  // on the right; row-reverse flips it so the chip lands on the right, label on the left.
  flexDirection: variant === 'ticker' ? 'row-reverse' : 'row',
  gap: theme.spacing(1),
  ...(variant === 'ticker' && isSmall ? {
    [theme.media.createObjectMediaQuery({ to: 2 })]: {
      gap: '3px',
    },
  } : {}),
}));

export const Period = styled(SmallText)<{ isSmall?: boolean; variant?: CardVariant }>(({ theme, isSmall, variant }) => ({
  color: theme.colors.neutrals.grey2,
  // v1 (default) keeps its legacy 13px; only the ticker bumps the non-small label to 14px.
  fontSize: isSmall ? 10 : (variant === 'ticker' ? 14 : 13),
  ...(variant === 'ticker' && isSmall ? {
    [theme.media.createObjectMediaQuery({ to: 2 })]: {
      fontSize: 14,
    },
  } : {}),
}));

export const Chip = styled(SmallText)<{ isGreen?: boolean; isSmall?: boolean; isGrey?: boolean; variant?: CardVariant }>(({ theme, isGreen, isSmall, isGrey, variant }) => ({
  color: theme.colors.neutrals.white,
  padding: `${theme.spacing(0.25)} ${theme.spacing(1)}`,
  borderRadius: theme.borderRadius.extraXLarge,
  background: isGrey ? theme.colors.neutrals.grey6 : isGreen ? theme.colors.brand.madlanGreen : '#FF6658',
  direction: 'ltr',
  // v1 (default) keeps its legacy 13px / SmallText weight; only the ticker uses 14px / bold.
  fontSize: isSmall ? 12 : (variant === 'ticker' ? 14 : 13),
  ...(variant === 'ticker' ? { fontWeight: 700 } : {}),
  justifySelf: 'flex-start',
  minWidth: 32,
  justifyContent: 'center',
  textAlign: 'center',
  ...(variant === 'ticker' && isSmall ? {
    [theme.media.createObjectMediaQuery({ to: 2 })]: {
      fontSize: 14,
    },
  } : {}),
}));
